import {Component, Input, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faShieldCheck} from '@fortawesome/pro-solid-svg-icons';
import {BicBadgeComponent} from '@buildinginformationcloud/bic-ui-components';

/**
 * Rotes „Admin"-Badge — Leitplanke für Oberflächen, die <b>keine</b> normale Nutzerfunktion sind.
 *
 * <p>Hintergrund: die zentrale Bereitstellung von myChecks steht optisch direkt neben gewöhnlichen
 * Check-Aktionen und hat mit dem geplanten Teilen für normale Nutzer nichts zu tun. Ohne eine
 * deutliche Markierung sind die beiden nicht auseinanderzuhalten.
 *
 * <p>Bewusst das satte {@code bg-red} statt des weicheren {@code bic-badge type="error"}
 * ({@code bg-red-50 text-red}): das hier ist keine Statusmeldung, sondern ein Warnhinweis.
 *
 * <h2>Verwendung in Popups</h2>
 * {@code bic-popup} hat keinen Header-Slot ({@code title} ist ein String). Mit
 * {@code [floating]="true"} positioniert sich das Badge selbst in die Kopfzeile, links neben den
 * ×-Button. Dafür muss das Popup {@code relative} in seiner {@code customClass} tragen:
 *
 * <pre>
 * &lt;bic-popup [customClass]="'w-[560px] relative'" title="myCheck teilen"&gt;
 *   &lt;app-admin-badge [floating]="true"&gt;&lt;/app-admin-badge&gt;
 *   ...
 * </pre>
 *
 * Die Positionierung lebt deshalb an genau einer Stelle — sonst driftet sie über die Aufrufer
 * auseinander.
 */
@Component({
  selector: 'app-admin-badge',
  standalone: true,
  imports: [FaIconComponent, BicBadgeComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div [class.absolute]="floating"
         [class.right-14]="floating"
         [class.top-6]="floating"
         [class.z-10]="floating">
      <bic-badge bgClass="bg-red" textClass="text-white">
        <fa-icon [icon]="faShieldCheck" iconLeft></fa-icon>
        {{ label }}
      </bic-badge>
    </div>
  `,
})
export class AdminBadgeComponent {
  /**
   * Positioniert das Badge absolut in der Kopfzeile eines {@code bic-popup}.
   * Das Popup braucht dafür {@code relative} in seiner {@code customClass}.
   */
  @Input() floating = false;

  /** Beschriftung. Standard „Admin"; für einen konkreteren Hinweis überschreibbar. */
  @Input() label = 'Admin';

  protected readonly faShieldCheck = faShieldCheck;
}
